import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import type { GetAccountUsageQuery } from '@frameio/graphql-client/generated';
import { useAccountRoleInfoFragment } from '@frameio/graphql-client/generated';
import { date as formatDate } from '@frameio/i18n';
import { getIsMountedStorageIncluded } from '@frameio/plan-management/plans/utils';
import { getLink } from '@frameio/shared/routing/getLink';
import TEXT from './generated/TEXT/MountedStorageAlertBanner';
import { StorageAlertBannerBase } from './StorageAlertBannerBase';
import { SupportButton } from './SupportButton';
import { UniversalLink } from './UniversalLink';

const ROLES_EXEMPT_FROM_BANNER_SUPPRESSION: ReadonlySet<string> = new Set([
  'account/owner',
  'account/admin',
]);

TEXT.DEFINE(
  {
    /** Title for billing managers */
    titleWithPermission: (
      mountedStorageCount: string,
      mountedStorageLimit: string
    ) =>
      `Mounted Storage Limit Exceeded (${mountedStorageCount} of ${mountedStorageLimit})`,

    /** Title for non-billing users */
    titleNoPermission: 'Mounted Storage Limit Exceeded',

    /** Message for Adobe-managed accounts without billing permissions */
    detailsNoPermissionAdobe: 'Contact your admin for assistance.',

    /** Message for non-billing users with grace period */
    detailsNoPermissionWithGrace: (ownerEmail: string, graceDate: Date) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance before {formatDate(graceDate, 'medium')} or account will
        be locked.
      </>
    ),

    /** Message for non-billing users without grace period */
    detailsNoPermissionNoGrace: (ownerEmail: string) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance.
      </>
    ),

    /** Message for Enterprise non-billing users */
    detailsNoPermissionEnterprise: (ownerEmail: string) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance.
      </>
    ),

    /** Message for Enterprise billing managers */
    detailsWithPermissionEnterprise: (manageStorageLink: string) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage Mounted Storage usage
        </UniversalLink>{' '}
        or <SupportButton>contact support</SupportButton> for assistance.
      </>
    ),

    /** Message for billing managers with grace period */
    detailsWithPermissionWithGrace: (
      manageStorageLink: string,
      graceDate: Date
    ) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage Mounted Storage usage
        </UniversalLink>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for billing managers without grace period */
    detailsWithPermissionNoGrace: (manageStorageLink: string) => (
      // eslint-disable-next-line prettier/prettier
      <><UniversalLink href={manageStorageLink}>Manage Mounted Storage usage</UniversalLink>.</>
    ),
  },
  { UniversalLink, SupportButton }
);

type MountedStorageAlertBannerProps = {
  account: NonNullable<GetAccountUsageQuery['account']>;
};

export function MountedStorageAlertBanner({
  account,
}: MountedStorageAlertBannerProps) {
  const projectsLink = getLink({
    page: 'settings',
    section: 'projects',
  });

  const isBannerSuppressed = useFeatureFlagEnabled(
    'v4.mounted_storage_suppress_banner'
  );
  const currentUserRoleId = useAccountRoleInfoFragment(account.id)?.data?.role
    ?.id;
  const isRoleExemptFromSuppression = ROLES_EXEMPT_FROM_BANNER_SUPPRESSION.has(
    currentUserRoleId ?? ''
  );

  if (isBannerSuppressed && !isRoleExemptFromSuppression) return null;

  return (
    <StorageAlertBannerBase
      account={account}
      featureGateKey="MOUNTED_STORAGE"
      getIsStorageAddOnIncluded={getIsMountedStorageIncluded}
      projectsLink={projectsLink}
      storageBytes={account.subscription?.limitedResources?.mountedStorageBytes}
      text={TEXT}
    />
  );
}
